import {useMemo} from 'react'

import {filterThreads} from './utils/filter-items'

import {usePullRequestBasePath, useThreads} from '../../stores/layout-selectors'
import {sortThreadsByDate, sortThreadsByDiffOrder} from './utils/sort-items'
import type {UseFilteredCommentsDataOptions} from './use-filtered-conversation-comments'
import {useFileTreeDiffSummaries} from '../../page-data/loaders/use-file-tree-diff-summaries'

const emptyArray = [] as const

/** Fetch and calculate filtered review thread items. */
export function useFilteredReviewThreads({filterState, sort}: UseFilteredCommentsDataOptions) {
  const threadPreviews = useThreads()

  const basePath = usePullRequestBasePath()
  const isDiffOrderSort = sort === 'diff-order'
  const diffSummariesQuery = useFileTreeDiffSummaries({basePath, select: data => data.diffs, enabled: isDiffOrderSort})
  const diffSummaries = diffSummariesQuery.data ?? emptyArray

  // Review thread mode displays threads as flat list without issue comments or review summaries
  const filteredThreads = useMemo(() => {
    const filteredItems = filterThreads(threadPreviews, filterState)
    const filteredSortedItems = isDiffOrderSort
      ? sortThreadsByDiffOrder(filteredItems, diffSummaries)
      : sortThreadsByDate(filteredItems, sort)

    return filteredSortedItems
  }, [filterState, threadPreviews, diffSummaries, sort, isDiffOrderSort])

  return {
    /** Thread items matching the conversation filter and sort direction. */
    filteredThreads,
    /** Total number of threads in this PR. */
    totalThreadsCount: threadPreviews.length,
    isLoading: isDiffOrderSort && diffSummariesQuery.isPending,
  }
}
